import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import type { ActionDefinitionT } from "@/types/database/actions";
import type { StatefulDiaryT } from "@/types/database/diaries";
import type { ProfileT } from "@/types/database/profiles";
import { ArrowLeft, ClipboardEdit, X } from "lucide-react";
import { Send } from "lucide-react";
import React from "react";
import { toast } from "sonner";

import { Link } from "@tanstack/react-router";
import useTakeAction from "../api/useTakeAction";
import useActionEmailHook from "../hooks/useActionEmailHook";
import { EmailWordsForm } from "./EmailWordsForm";

export interface Props {
	actionId: string;
	action: ActionDefinitionT;
	diary: StatefulDiaryT;
	profile: ProfileT;
}

const ActionEmail = (props: Props) => {
	const { isPending } = useTakeAction();
	const { form, onSubmit } = useActionEmailHook(props);

	const emailOptions = props.diary.diary_access?.map((sw) => ({
		value: sw.email,
		label: sw.email,
	}));

	return (
		<ui.Dialog>
			<ui.DialogContent>
				<EmailWordsForm
					diaryKey={props.diary.diary_status}
					actionKey={props.action.action_key}
					onEmailUpdate={(body) => form.setValue("body", body)}
					diary={props.diary}
				/>
			</ui.DialogContent>
			<ui.Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)} data-email-form>
					<div className="flex flex-col space-y-2 h-full">
						<div className="flex justify-between items-center">
							<div className="flex items-center">
								<ui.Button variant="ghost" size="icon" asChild>
									<Link
										to="/patients/$diaryId"
										params={{ diaryId: props.diary.diary_id }}
									>
										<ArrowLeft />
									</Link>
								</ui.Button>
							</div>
							<div className="flex flex-row gap-2">
								<ui.DialogTrigger asChild>
									<ui.Button size="sm" type="button" variant="outline">
										<ClipboardEdit />
										Edit
									</ui.Button>
								</ui.DialogTrigger>
								<ui.Button
									disabled={isPending}
									onClick={() => {
										if (Object.keys(form.formState.errors).length) {
											console.error(form.formState.errors);
											toast.error("Please fill in all fields");
										}
									}}
									size="sm"
									type="submit"
									data-testid="send-button"
								>
									<Send />
									{isPending ? "Sending" : "Send"}
								</ui.Button>
							</div>
						</div>
						<div>
							<fc.Recipient type="email" name="from_email" label="From" />
							<hr className="my-1 border-slate-300" />
							<div className="flex justify-between items-center gap-1">
								<fc.SelectRecipient
									options={emailOptions}
									name="cc_email"
									label="Cc"
								/>
								<ui.Button
									type="button"
									onClick={() => form.setValue("cc_email", "")}
									variant="ghost"
									size="icon"
									className="rounded-sm"
								>
									<X />
								</ui.Button>
							</div>
							<hr className="my-1 border-slate-300" />
							<div className="flex justify-between items-center gap-1">
								<fc.Recipient
									type="text"
									disabled={true}
									name="to_email"
									label="To"
								/>
								<ui.Button
									type="button"
									onClick={() => form.setValue("to_email", "")}
									variant="ghost"
									size="icon"
									className="rounded-sm"
								>
									<X />
								</ui.Button>
							</div>
							<hr className="my-1 border-slate-300" />
							<fc.Recipient
								type="text"
								disabled={false}
								name="subject"
								label="Subject"
							/>
						</div>

						<fc.Textarea
							name="body"
							placeholder="Email Body"
							className="h-full min-h-[400px]"
							{...form.register("body")}
						/>
					</div>
				</form>
			</ui.Form>
		</ui.Dialog>
	);
};

export default ActionEmail;
